import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import Aggrid from "../../framework/AgGrid";
import Button from "../../framework/Button";
import Shortcut from "../../framework/Shortcut";
import { fetchReportData } from "../../services/report-services";
import Drawer from "../../framework/drawer/Drawer";
import classes from "./ReportGrid.module.css";
import { fetchColumnDefs, fetchColumnDefsFns } from "./reportUtils";
import { defaultReportColumnDefs } from "./reportConstants";
import {
  initializeColumnDefs,
  rowDown,
  rowUp,
  selectRow,
} from "../../utils/gridUtils";
import ColumnFilters from "./ColumnFilters";
import GlobalContext from "../../framework/GlobalContext";
import ReportContext from "./ReportContext";
import { useSelector } from "react-redux";
import { isEmpty } from "../../utils/utils";
import { useDispatch } from "react-redux";
import _ from "lodash";

const ReportGrid = forwardRef(
  (
    {
      identifier,
      showColumnCTA = true,
      showFilterCTA = true,
      showFilterOnMount = false,
      rowLevelCTAs = [],
      filters,
      // filterParams,
      filterStates,
      getRowClass,
      defaultFilterDrawerWidth = 300, // to set width of filter drawer
      defaultColumnDrawerWidth = 200, // to set width of column drawer
      // summaryTotals = {},
      // getGridApi = () => {},
      onFilterShowClick = () => {},
      onColumnShowClick = () => {},
      // currentRow = () => {},
      reportSummary = () => {},
      onCellClicked = () => {},
      onCellFocused = () => {},
      onRowDown = () => {},
      onRowUp = () => {},
    },
    ref
  ) => {
    const [reportRowData, setReportRowData] = useState([]);
    const [api, setApi] = useState(null);
    const { currentComponent, setCurrentComponent } = useContext(GlobalContext);
    const {
      showColumnDrawer,
      showFilterDrawer,
      setShowFilterDrawer,
      setShowColumnDrawer,
      reportGridRef,
      focusGrid,
    } = useContext(ReportContext);
    const dispatch = useDispatch();
    const { data } = useSelector((state) => {
      return state.commonReducer.tabs[state.commonReducer?.activeTabIndex];
    });
    const onGridReady = (params) => {
      setApi(params.api);
    };
    const fetchData = useCallback(
      async (identifier, filters) => {
        const result = await fetchReportData({ identifier, ...filters });
        setReportRowData(result.rows || []);
        if (result.summary) reportSummary(result.summary);
        if (result?.rows?.length)
          dispatch({
            type: "add_report_data_to_redux",
            payload: {
              rows: result.rows,
              summary: result.summary,
              filters: filterStates,
            },
          });
      },
      [dispatch, filterStates, reportSummary]
    );
    const applyFilterHandler = useCallback(
      async (filters) => {
        setReportRowData(null);
        if (filters.isFilterChanged)
          dispatch({
            type: "update_current_focus",
            payload: null,
          });
        fetchData(identifier, filters);
        setShowFilterDrawer(false);
        setTimeout(() => {
          setCurrentComponent("grid_reports");
        }, 300);
      },
      [
        dispatch,
        fetchData,
        identifier,
        setCurrentComponent,
        setShowFilterDrawer,
      ]
    );
    const columnHandler = useCallback(() => {
      onColumnShowClick(showColumnDrawer);
    }, [onColumnShowClick, showColumnDrawer]);
    const filterHandler = useCallback(() => {
      onFilterShowClick(showFilterDrawer);
    }, [onFilterShowClick, showFilterDrawer]);
    const gridListener = useCallback(
      (e) => {
        const key = e.key;
        const ctrlKey = e.ctrlKey;
        if (key === "ArrowDown") {
          rowDown(api, ctrlKey);
          onRowDown(api.getSelectedNodes()[0].data);
          return;
        }
        if (key === "ArrowUp") {
          rowUp(api, ctrlKey);
          onRowUp(api.getSelectedNodes()[0].data);
          return;
        }
      },
      [api, onRowDown, onRowUp]
    );
    const updateCurrentRowIndex = useCallback(
      (api) => {
        if (api.getSelectedNodes())
          dispatch({
            type: "update_current_focus",
            payload: {
              id: "grid_reports",
              rowIndex: api.getSelectedNodes()[0].rowIndex,
            },
          });
      },
      [dispatch]
    );
    const debouncedFn = _.debounce(updateCurrentRowIndex, 300);
    const selectionChangedHandler = (params) => {
      // const selectedNode = params.api.getSelectedNodes()[0];
      // currentRow(selectedNode); //debounce
      debouncedFn(params.api);
    };
    const columnFilterHandler = useCallback(
      (updatedColumnDefs) => {
        localStorage.setItem(
          `${identifier}`,
          JSON.stringify(updatedColumnDefs)
        );
        api?.setColumnDefs([...updatedColumnDefs]);
      },
      [api, identifier]
    );
    const onColumnMovedHandler = useCallback(
      (params) => {
        columnFilterHandler(params.api.getColumnDefs());
      },
      [columnFilterHandler]
    );
    const onColumnResizedHandler = useCallback(
      (params) => {
        columnFilterHandler(params.api.getColumnDefs());
      },
      [columnFilterHandler]
    );
    const rowDataChangedHandler = useCallback(
      (params) => {
        if (params.api.getDisplayedRowCount()) {
          focusGrid(params.api);
        }
        // else {
        //   currentRow(null);
        // }
      },
      [focusGrid]
    );
    useImperativeHandle(
      ref,
      () => {
        return {
          quickExport: (params) => {
            return api?.exportDataAsCsv(params);
          },
          applyFilters: (filters) => applyFilterHandler(filters),
          getCurrentRowData: () =>
            api.getDisplayedRowCount() ? api.getSelectedNodes()[0]?.data : null,
        };
      },
      [api, applyFilterHandler]
    );
    useEffect(() => {
      if (!isEmpty(data) && data.rows?.length) {
        setReportRowData(data.rows);
      }
    }, [data]);
    // useEffect(() => {
    //   if (filterParams) {
    //     applyFilterHandler(filterParams);
    //   }
    // }, [applyFilterHandler, filterParams]);
    useEffect(() => {
      if (identifier && api) {
        initializeColumnDefs(
          api,
          identifier,
          fetchColumnDefs(identifier),
          fetchColumnDefsFns(identifier)
        );
      }
    }, [api, identifier]);
    useEffect(() => {
      if (currentComponent === "grid_reports")
        document.addEventListener("keyup", gridListener, false);
      return () => {
        document.removeEventListener("keyup", gridListener, false);
      };
    }, [currentComponent, gridListener]);
    useEffect(() => {
      if (!showColumnDrawer && !showFilterDrawer)
        setCurrentComponent("grid_reports");
    }, [setCurrentComponent, showColumnDrawer, showFilterDrawer]);
    useEffect(() => {
      if (showColumnCTA) columnHandler(showColumnDrawer);
    }, [columnHandler, showColumnCTA, showColumnDrawer]);
    useEffect(() => {
      if (showFilterCTA) filterHandler(showFilterDrawer);
    }, [filterHandler, showFilterCTA, showFilterDrawer]);
    useEffect(() => {
      if (isEmpty(data) && showFilterCTA && showFilterOnMount) {
        setShowFilterDrawer(showFilterOnMount);
      }
    }, [data, setShowFilterDrawer, showFilterCTA, showFilterOnMount]);
    useEffect(() => {
      return () => {
        setCurrentComponent(null);
      };
    }, [setCurrentComponent]);
    return (
      <div className={classes["report-container"]}>
        <div className={classes["report-grid"]}>
          <Aggrid
            id="grid_reports"
            ref={reportGridRef}
            defaultColDef={defaultReportColumnDefs}
            rowData={reportRowData}
            onGridReady={onGridReady}
            onRowDataChanged={rowDataChangedHandler}
            onSelectionChanged={selectionChangedHandler}
            onCellClicked={onCellClicked}
            onCellFocused={onCellFocused}
            onColumnMoved={onColumnMovedHandler}
            onColumnResized={onColumnResizedHandler}
            getRowClass={getRowClass}
            onFilterChanged={(params) => {
              if (params.api.getDisplayedRowCount() > 0)
                return selectRow(params.api, 0);
            }}
          />
          <Drawer
            position="left"
            show={showColumnCTA && showColumnDrawer}
            width={defaultColumnDrawerWidth}
          >
            <ColumnFilters
              columnDefs={showColumnDrawer ? api?.getColumnDefs() : []}
              onChange={columnFilterHandler}
            />
          </Drawer>
          <Drawer
            position="right"
            show={showFilterCTA && showFilterDrawer}
            width={defaultFilterDrawerWidth}
            mountOnEnter={false} //set to false for initial loading
            unmountOnExit={false} //unmountOnExit set to false to preserve filters
          >
            {filters}
          </Drawer>
        </div>
        <div className={classes["report-cta-container"]}>
          <div
            className={`${classes["column-cta"]} ${
              showColumnCTA && classes["visible"]
            } ${showColumnDrawer && classes["drawer-opened"]} `}
          >
            <Button
              label="Columns"
              startIcon={<Shortcut keys={["Alt", "O"]} />}
              onClick={() => setShowColumnDrawer((p) => !p)}
            />
          </div>
          <div
            className={`${classes["row-level-ctas"]} ${
              rowLevelCTAs?.length && classes["visible"]
            } `}
          >
            {rowLevelCTAs.map((cta, index) => (
              <div key={index} className={classes["row-level-cta"]}>
                {cta}
                {index !== rowLevelCTAs.length - 1 && (
                  <div className={classes["cta-divider"]} />
                )}
              </div>
            ))}
          </div>
          <div
            className={`${classes["filter-cta"]} ${
              showFilterCTA && classes["visible"]
            } ${showFilterDrawer && classes["drawer-opened"]}`}
          >
            <Button
              label="Filter"
              startIcon={<Shortcut keys={["Alt", "F"]} />}
              onClick={() => setShowFilterDrawer((p) => !p)}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default ReportGrid;
